/**
 * BulkTransferDialog - Modal de transferência em massa
 * 
 * RELEASE: 2.0.0 - 2025-01-22
 * 
 * Wizard multi-step premium para transferência atômica de:
 * - Conversas (agente responsável)
 * - Negócios (responsável interno ou vendedor)
 * - Contatos (vendedor)
 * 
 * Layout 2 colunas seguindo padrão CreateCampaignDialog:
 * - Sidebar com navegação interativa e resumo em tempo real
 * - Área principal com header, conteúdo scrollável e footer fixo
 * 
 * Steps:
 * 1. Selecionar tipo de transferência
 * 2. Selecionar origem (de quem transferir)
 * 3. Selecionar destino (para quem transferir)
 * 4. Aplicar filtros opcionais
 * 5. Preview e confirmação
 * 6. Execução com progresso
 */

import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Loader2, 
  X,
  Shuffle,
  UserMinus,
  UserPlus,
  Filter,
  Eye,
  CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBulkTransfer, TransferType, TRANSFER_TYPE_LABELS } from '@/hooks/useBulkTransfer';

// Importar steps do wizard
import { TransferTypeStep } from './steps/TransferTypeStep';
import { TransferSourceStep } from './steps/TransferSourceStep';
import { TransferTargetStep } from './steps/TransferTargetStep';
import { TransferFiltersStep } from './steps/TransferFiltersStep';
import { TransferPreviewStep } from './steps/TransferPreviewStep';
import { TransferProgressStep } from './steps/TransferProgressStep';

// =============================================================================
// TIPOS E INTERFACES
// =============================================================================

interface BulkTransferDialogProps {
  /** Controle de abertura do dialog */
  open: boolean;
  /** Callback para fechar dialog */
  onOpenChange: (open: boolean) => void;
  /** Contexto de origem: 'queue' (fila) ou 'deals' (negócios) */
  context: 'queue' | 'deals';
  /** Se deve mostrar opções de CRM (negócios/vendedores) */
  showDealOptions?: boolean;
  /** Tipo de transferência pré-selecionado (opcional) */
  initialTransferType?: TransferType;
}

// =============================================================================
// CONFIGURAÇÃO DOS STEPS COM ÍCONES
// =============================================================================

interface StepConfig {
  id: number;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  description: string;
}

const STEPS: StepConfig[] = [
  { 
    id: 1, 
    label: 'Tipo de Transferência', 
    shortLabel: 'Tipo',
    icon: Shuffle,
    description: 'Escolha o que deseja transferir'
  },
  { 
    id: 2, 
    label: 'Origem', 
    shortLabel: 'Origem',
    icon: UserMinus,
    description: 'De quem transferir os registros'
  },
  { 
    id: 3, 
    label: 'Destino', 
    shortLabel: 'Destino',
    icon: UserPlus,
    description: 'Para quem transferir os registros'
  },
  { 
    id: 4, 
    label: 'Filtros', 
    shortLabel: 'Filtros',
    icon: Filter,
    description: 'Refine a seleção com filtros opcionais'
  },
  { 
    id: 5, 
    label: 'Preview', 
    shortLabel: 'Preview',
    icon: Eye,
    description: 'Revise e confirme a transferência'
  },
  { 
    id: 6, 
    label: 'Execução', 
    shortLabel: 'Resultado',
    icon: CheckCircle2,
    description: 'Acompanhe o resultado da operação'
  },
];

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export function BulkTransferDialog({
  open,
  onOpenChange,
  context,
  showDealOptions = true,
  initialTransferType,
}: BulkTransferDialogProps) {
  const {
    wizardState,
    nextStep,
    prevStep,
    goToStep,
    resetWizard,
    setTransferType,
    setFromId,
    setToId,
    setTargetMode,   // v2.3.0
    setQueueId,      // v2.3.0
    setFilters,
    executePreview,
    executeTransfer,
    isPreviewLoading,
    isExecuteLoading,
  } = useBulkTransfer();

  // v2.3.0: Extrair targetMode e queueId do estado
  const { step, transferType, fromId, toId, targetMode, queueId, previewResult, executionResult, error } = wizardState;

  // Step atual configuração
  const currentStep = STEPS.find(s => s.id === step) || STEPS[0];
  const StepIcon = currentStep.icon;

  // =========================================================================
  // EFEITOS
  // =========================================================================

  // Resetar wizard ao abrir/fechar
  useEffect(() => {
    if (open) {
      resetWizard();
      // Se tiver tipo inicial, aplicar
      if (initialTransferType) {
        setTransferType(initialTransferType);
      }
    }
  }, [open, resetWizard, initialTransferType, setTransferType]);

  // =========================================================================
  // VALIDAÇÃO DE NAVEGAÇÃO
  // =========================================================================

  const canGoNext = (): boolean => {
    switch (step) {
      case 1: return !!transferType;
      case 2: return !!fromId;
      case 3: 
        // v2.3.0: Validação depende do modo
        if (targetMode === 'queue') {
          return !!queueId;
        }
        return !!toId && fromId !== toId;
      case 4: return true; // Filtros são opcionais
      case 5: return !!previewResult && previewResult.count > 0;
      default: return false;
    }
  };

  const canGoBack = (): boolean => {
    return step > 1 && step < 6;
  };

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleNext = async () => {
    if (step === 4) {
      // Antes de ir para preview, executar preview
      await executePreview();
      nextStep();
    } else if (step === 5) {
      // Executar transferência
      await executeTransfer();
      nextStep();
    } else {
      nextStep();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Handler para navegação via sidebar
  const handleStepClick = (targetStep: number) => {
    // Permite voltar para steps anteriores, mas não avançar além do atual
    // E não permite ir diretamente para o step de execução
    if (targetStep < step && targetStep !== 6) {
      goToStep(targetStep);
    }
  };

  // =========================================================================
  // RENDER DO STEP ATUAL
  // =========================================================================

  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return (
          <TransferTypeStep
            context={context}
            showDealOptions={showDealOptions}
            selectedType={transferType}
            onSelectType={setTransferType}
          />
        );
      case 2:
        return (
          <TransferSourceStep
            transferType={transferType!}
            selectedId={fromId}
            onSelectId={setFromId}
          />
        );
      case 3:
        return (
          <TransferTargetStep
            transferType={transferType!}
            selectedId={toId}
            excludeId={fromId}
            targetMode={targetMode}
            queueId={queueId}
            onSelectId={setToId}
            onTargetModeChange={setTargetMode}
            onQueueIdChange={setQueueId}
          />
        );
      case 4:
        return (
          <TransferFiltersStep
            transferType={transferType!}
            filters={wizardState.filters}
            onFiltersChange={setFilters}
          />
        );
      case 5:
        return (
          <TransferPreviewStep
            transferType={transferType!}
            fromId={fromId!}
            toId={toId!}
            filters={wizardState.filters}
            previewResult={previewResult}
            isLoading={isPreviewLoading}
            error={error}
          />
        );
      case 6:
        return (
          <TransferProgressStep
            transferType={transferType!}
            executionResult={executionResult}
            isLoading={isExecuteLoading}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          
          {/* ============================================================ */}
          {/* SIDEBAR - Navegação de Steps + Resumo */}
          {/* ============================================================ */}
          <aside 
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Header da Sidebar */}
            <div className="relative z-10 p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Shuffle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Transferência em Massa</h2>
                  <p className="text-xs text-white/60">
                    {step} de {STEPS.length} etapas
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de Steps Clicável */}
            <nav className="relative z-10 flex-1 p-4 space-y-1 overflow-y-auto">
              {STEPS.map((s) => {
                const isActive = step === s.id;
                const isCompleted = step > s.id;
                const Icon = s.icon;

                return (
                  <button
                    key={s.id}
                    onClick={() => handleStepClick(s.id)}
                    disabled={s.id > step || s.id === 6}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all",
                      "hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed",
                      isActive && "bg-white/10 border border-white/20",
                      !isActive && !isCompleted && "text-white/50"
                    )}
                  >
                    {/* Círculo indicador */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      "border-2 transition-colors",
                      isCompleted && "bg-green-500 border-green-500 text-white",
                      isActive && "bg-white border-white text-primary",
                      !isActive && !isCompleted && "border-white/30 text-white/50"
                    )}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>

                    {/* Texto do step */}
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        "font-medium text-sm block truncate",
                        isActive && "text-white",
                        isCompleted && "text-white/70",
                        !isActive && !isCompleted && "text-white/50"
                      )}>
                        {s.label}
                      </span>
                    </div>

                    {/* Badge de contagem no preview */}
                    {isCompleted && s.id === 5 && previewResult?.count && (
                      <Badge className="text-xs bg-white/20 text-white border-0 hover:bg-white/20">
                        {previewResult.count}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Resumo em Tempo Real */}
            <div className="relative z-10 p-4 border-t border-white/10 bg-white/5">
              <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                Resumo
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Tipo</span>
                  <span className="font-medium text-white truncate ml-2">
                    {transferType ? TRANSFER_TYPE_LABELS[transferType] : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Registros</span>
                  <span className="font-medium text-white">
                    {previewResult?.count ?? '-'}
                  </span>
                </div>
              </div>
            </div>
          </aside>

          {/* ============================================================ */}
          {/* CONTEÚDO PRINCIPAL */}
          {/* ============================================================ */}
          <main className="flex flex-col h-full min-h-0">
            {/* Header do Step */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStep.label}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStep.description}
                  </DialogDescription>
                </div>
                <Badge variant="outline" className="md:hidden shrink-0">
                  {step}/{STEPS.length}
                </Badge>
              </div>

              {/* Stepper Mobile */}
              <div className="flex items-center gap-1.5 mt-4 md:hidden">
                {STEPS.map((s) => (
                  <div
                    key={s.id}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors",
                      step > s.id && "bg-green-500",
                      step === s.id && "bg-primary",
                      step < s.id && "bg-muted"
                    )}
                  />
                ))}
              </div>
            </DialogHeader>

            {/* Área de Conteúdo Scrollável */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="px-6 py-5">
                {renderCurrentStep()}
              </div>
            </div>

            {/* Footer com Navegação */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-background flex items-center justify-between">
              {canGoBack() ? (
                <Button 
                  variant="ghost" 
                  onClick={prevStep} 
                  disabled={isPreviewLoading || isExecuteLoading}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Voltar
                </Button>
              ) : (
                <div />
              )}

              <span className="text-sm text-muted-foreground hidden sm:block">
                Etapa {step} de {STEPS.length}
              </span>

              <div className="flex gap-2">
                {step < 6 && (
                  <Button variant="ghost" onClick={handleClose}>
                    Cancelar
                  </Button>
                )}

                {step < 6 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canGoNext() || isPreviewLoading || isExecuteLoading}
                  >
                    {isPreviewLoading || isExecuteLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : step === 5 ? (
                      <Check className="h-4 w-4 mr-2" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    {step === 5 ? 'Confirmar Transferência' : 'Próximo'}
                  </Button>
                ) : (
                  <Button onClick={handleClose}>
                    <X className="h-4 w-4 mr-2" />
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
